/**
 * StudioBookPublic — Phase 5b public Studio booking marketplace.
 *
 * External clients land here, browse bookable resources, pick a slot,
 * fill their contact info, and submit. The booking lands in the
 * existing studio_bookings collection as pending + source=public_marketplace
 * and the Studio team confirms it from their existing admin UI.
 */
import React, { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Video, Mic, Edit3, CalendarCheck, Clock, Users, DollarSign, ArrowLeft, Loader2, CheckCircle2, Search } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_META = {
  live_room:        { label: 'Live Room',       icon: Video },
  edit_suite:       { label: 'Edit Suite',      icon: Edit3 },
  podcast_booth:    { label: 'Podcast Booth',   icon: Mic },
  rehearsal_room:   { label: 'Rehearsal Room',  icon: Users },
  control_room:     { label: 'Control Room',    icon: Video },
  green_room:       { label: 'Green Room',      icon: Users },
  outside_broadcast:{ label: 'Outside Broadcast', icon: Video },
};

const fmtCurrency = (n, c = 'USD') => `${c} ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtTime = (iso) => new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

const StudioBookPublic = () => {
  const [search, setSearch] = useState('');
  const [bookingFor, setBookingFor] = useState(null);
  const [confirmation, setConfirmation] = useState(null);

  const { data: resources = [], isLoading } = useQuery({
    queryKey: ['studio-public-resources'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/studio/public/resources`);
      if (!res.ok) throw new Error('Failed to load');
      return res.json();
    },
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return resources;
    return resources.filter((r) =>
      (r.name + ' ' + r.description + ' ' + (TYPE_META[r.type]?.label || r.type)).toLowerCase().includes(q)
    );
  }, [resources, search]);

  if (confirmation) {
    return <ConfirmationView confirmation={confirmation} onReset={() => { setConfirmation(null); setBookingFor(null); }} />;
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="studio-public">
      {/* Hero */}
      <header className="bg-gradient-to-br from-violet-900 via-fuchsia-800 to-violet-700 text-white">
        <div className="max-w-5xl mx-auto px-4 py-12 sm:py-16">
          <div className="flex items-center gap-3 mb-4">
            <Video className="h-7 w-7 text-fuchsia-200" />
            <span className="text-xs font-semibold tracking-[0.3em] uppercase text-fuchsia-200">The Studio</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">Book a creative space for your next shoot</h1>
          <p className="mt-3 text-sm sm:text-base text-fuchsia-100 max-w-xl">
            Live rooms, edit suites, podcast booths and more. Pick a slot, tell us about your project, and our team will confirm within 24 hours.
          </p>
          <div className="mt-6 flex items-center gap-2 max-w-md">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-fuchsia-200" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search rooms, suites, booths…"
                className="pl-9 bg-white/10 border-white/20 text-white placeholder:text-fuchsia-200 focus:bg-white/20"
                data-testid="studio-public-search"
              />
            </div>
          </div>
        </div>
      </header>

      {/* Grid */}
      <main className="max-w-5xl mx-auto px-4 py-8">
        {isLoading ? (
          <div className="text-center py-16 text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline mr-2" />Loading rooms…</div>
        ) : filtered.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center text-slate-500">
              <Video className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              {resources.length === 0
                ? <p>No spaces are open for public booking right now. Please check back soon.</p>
                : <p>No rooms match "{search}". Try a different search.</p>}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filtered.map((r) => {
              const Icon = TYPE_META[r.type]?.icon || Video;
              return (
                <Card key={r.id} className="hover:shadow-lg transition-shadow overflow-hidden" data-testid={`studio-public-resource-${r.id}`}>
                  {r.photos[0] ? (
                    <div className="h-40 bg-cover bg-center" style={{ backgroundImage: `url(${r.photos[0]})` }} />
                  ) : (
                    <div className="h-40 bg-gradient-to-br from-violet-100 to-fuchsia-100 flex items-center justify-center">
                      <Icon className="h-12 w-12 text-violet-400" />
                    </div>
                  )}
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-base flex items-center gap-2">
                          <Icon className="h-4 w-4 text-violet-600" />
                          {r.name}
                        </CardTitle>
                        <Badge variant="outline" className="text-[10px] mt-1">{TYPE_META[r.type]?.label || r.type}</Badge>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-violet-700">{fmtCurrency(r.hourly_rate, r.currency)}</p>
                        <p className="text-[10px] text-slate-500">per hour</p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {r.description && <p className="text-xs text-slate-600 mb-3 line-clamp-2">{r.description}</p>}
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-slate-500 flex items-center gap-2">
                        {r.capacity > 0 && <span className="flex items-center gap-1"><Users className="h-3 w-3" />{r.capacity}</span>}
                      </div>
                      <Button size="sm" onClick={() => setBookingFor(r)} data-testid={`studio-public-book-${r.id}`}>
                        <CalendarCheck className="h-3.5 w-3.5 mr-1" /> Book
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      {bookingFor && (
        <BookingDialog
          resource={bookingFor}
          onClose={() => setBookingFor(null)}
          onConfirmed={(c) => setConfirmation(c)}
        />
      )}
    </div>
  );
};


function BookingDialog({ resource, onClose, onConfirmed }) {
  const today = new Date();
  const isoDate = today.toISOString().slice(0, 10);

  const [form, setForm] = useState({
    date: isoDate,
    start_time: '10:00',
    end_time: '12:00',
    client_name: '',
    client_email: '',
    client_phone: '',
    purpose: '',
    notes: '',
  });

  const startISO = useMemo(() => `${form.date}T${form.start_time}:00`, [form.date, form.start_time]);
  const endISO = useMemo(() => `${form.date}T${form.end_time}:00`, [form.date, form.end_time]);
  const hours = useMemo(() => {
    try {
      const a = new Date(startISO).getTime();
      const b = new Date(endISO).getTime();
      return Math.max(0, (b - a) / 3600000);
    } catch { return 0; }
  }, [startISO, endISO]);
  const estimatedCost = +(hours * (resource.hourly_rate || 0)).toFixed(2);

  const { data: availability } = useQuery({
    queryKey: ['studio-public-availability', resource.id, form.date],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/studio/public/resources/${resource.id}/availability?date=${form.date}`);
      if (!res.ok) return { blocked: [] };
      return res.json();
    },
    enabled: !!resource?.id && !!form.date,
  });

  const submitMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/api/studio/public/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          resource_id: resource.id,
          start_at: new Date(startISO).toISOString(),
          end_at: new Date(endISO).toISOString(),
          client_name: form.client_name,
          client_email: form.client_email,
          client_phone: form.client_phone,
          purpose: form.purpose,
          notes: form.notes,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = typeof data.detail === 'object' ? (data.detail.message || JSON.stringify(data.detail)) : (data.detail || 'Booking failed');
        throw new Error(detail);
      }
      return data;
    },
    onSuccess: (data) => {
      toast.success('Booking request submitted!');
      onConfirmed?.(data);
    },
    onError: (e) => toast.error(e.message),
  });

  const valid = form.client_name && form.client_email && hours > 0 && estimatedCost >= 0;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="studio-public-book-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CalendarCheck className="h-4 w-4 text-violet-600" /> Book {resource.name}</DialogTitle>
          <DialogDescription>Pick a slot and tell us who you are. We'll confirm by email.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <div className="col-span-3">
              <Label>Date</Label>
              <Input type="date" value={form.date} min={isoDate} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="studio-public-date" />
            </div>
            <div>
              <Label>From</Label>
              <Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="studio-public-start" />
            </div>
            <div>
              <Label>To</Label>
              <Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="studio-public-end" />
            </div>
            <div className="text-xs text-slate-600 flex items-end pb-2">
              <Clock className="h-3 w-3 mr-1" /> {hours.toFixed(1)} hr
            </div>
          </div>

          {availability?.blocked?.length > 0 && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
              <p className="font-medium mb-0.5">Already booked on this date:</p>
              <ul className="space-y-0.5">
                {availability.blocked.slice(0, 5).map((b, i) => (
                  <li key={i}>· {fmtTime(b.start_at)} – {new Date(b.end_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="rounded-lg bg-violet-50 p-3 flex items-center justify-between">
            <div className="text-xs text-violet-900">
              <DollarSign className="h-3 w-3 inline mr-1" />
              {hours.toFixed(1)} hrs × {fmtCurrency(resource.hourly_rate, resource.currency)}
            </div>
            <div className="text-lg font-bold text-violet-800">{fmtCurrency(estimatedCost, resource.currency)}</div>
          </div>

          <div className="grid grid-cols-2 gap-2 border-t pt-3">
            <div className="col-span-2">
              <Label>Your name *</Label>
              <Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} placeholder="Jane Doe" data-testid="studio-public-name" />
            </div>
            <div>
              <Label>Email *</Label>
              <Input type="email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} placeholder="jane@example.com" data-testid="studio-public-email" />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={form.client_phone} onChange={(e) => setForm({ ...form, client_phone: e.target.value })} placeholder="+1 555…" data-testid="studio-public-phone" />
            </div>
            <div className="col-span-2">
              <Label>Project / Purpose</Label>
              <Input value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} placeholder="e.g. Podcast recording – Episode 5" data-testid="studio-public-purpose" />
            </div>
            <div className="col-span-2">
              <Label>Anything else?</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Equipment needed, special requests…" data-testid="studio-public-notes" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => submitMutation.mutate()} disabled={!valid || submitMutation.isPending} data-testid="studio-public-submit">
            {submitMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
            Submit request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ConfirmationView({ confirmation, onReset }) {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="studio-public-confirmation">
      <Card className="max-w-md w-full text-center">
        <CardContent className="pt-10 pb-8 space-y-4">
          <div className="w-16 h-16 rounded-full bg-violet-100 flex items-center justify-center mx-auto">
            <CheckCircle2 className="h-8 w-8 text-violet-700" />
          </div>
          <h2 className="text-xl font-bold text-slate-900">Request received!</h2>
          <p className="text-sm text-slate-600">{confirmation.message}</p>
          <div className="bg-slate-50 rounded-lg p-4 text-left text-sm space-y-1">
            <p className="text-xs text-slate-400 uppercase tracking-wide">Booking Reference</p>
            <p className="text-base font-mono font-bold text-violet-700" data-testid="studio-public-confirmation-ref">{confirmation.booking_reference}</p>
            <p className="mt-2"><b>Room:</b> {confirmation.resource_name}</p>
            <p><b>When:</b> {fmtTime(confirmation.start_at)} – {new Date(confirmation.end_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
            <p><b>Estimated:</b> {fmtCurrency(confirmation.estimated_cost, confirmation.currency)}</p>
          </div>
          <p className="text-xs text-slate-400">Please save this reference. We'll email you to confirm.</p>
          <Button variant="outline" onClick={onReset} className="mt-2">
            <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Browse more rooms
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}


export default StudioBookPublic;
